import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { getLoginEpoch } from "@/lib/welcomeSignal";

// Detect the rendering platform. Apple browsers (iOS/iPadOS Safari, and desktop Safari) CANNOT
// render alpha-transparent WebM — Safari now *plays* WebM but drops its VP9 alpha, exposing the
// underlying green colour-plane and showing native video controls. So we must NOT feature-detect
// via canPlayType; we route Apple → transparent animated APNG <img>, everyone else → WebM <video>.
function detectPlatform() {
  try {
    const ua = navigator.userAgent || "";
    const iOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
    const isSafari = /^((?!chrome|android|crios|fxios|edg|opr|samsungbrowser).)*safari/i.test(ua);
    return (iOS || isSafari) ? "ios" : "android";
  } catch {
    return "android";
  }
}

// Transparent welcome overlay. Renders ONLY right after a successful customer login, plays once
// per login, then fades out. No sessionStorage / "seen" gate — a plain refresh does not replay it
// (module epoch resets to 0 on reload), but every fresh login does.
//
// Same approved animation on both platforms, rendered with the technically-correct transparent format:
//   • Android / desktop-web → transparent WebM (VP9 + alpha) via <video> (autoplay, muted, playsInline, no controls).
//   • iPhone / iOS / Safari  → transparent animated APNG via <img> (real per-frame alpha, no <video>, no play button).
// Each platform is governed by its OWN independent admin flag (welcome_animation_android / _ios).
export default function WelcomeAnimation() {
  // Captured at render time via a PURE read so React 18 StrictMode's double-invocation is safe.
  const [armed] = useState(() => getLoginEpoch() > 0);
  const [platform] = useState(detectPlatform); // "ios" → APNG <img>; "android" → WebM <video>
  const videoRef = useRef(null);
  const [show, setShow] = useState(false);
  const [leaving, setLeaving] = useState(false);

  useEffect(() => {
    if (!armed) return; // mounted from a refresh/navigation, not a fresh login → do not play
    let cancelled = false;
    api.get(`/public/welcome-animation?platform=${platform}`)
      .then((r) => { if (!cancelled && r.data?.status === "LIVE") setShow(true); }) // only LIVE shows the splash
      .catch(() => {}); // flag unavailable → skip silently, app unaffected
    return () => { cancelled = true; };
  }, [armed, platform]);

  const dismiss = () => { setLeaving(true); setTimeout(() => setShow(false), 550); };

  useEffect(() => {
    if (!show) return;
    let timer;
    if (platform !== "ios") {
      const v = videoRef.current;
      if (v) {
        v.muted = true; // muted property (not just the attribute) is required for autoplay
        try { v.currentTime = 0; } catch { /* ignore */ }
        const p = v.play?.();
        if (p?.catch) p.catch(() => setTimeout(dismiss, 4000)); // autoplay blocked → brief hold then fade
      }
      timer = setTimeout(dismiss, 15000); // backstop if 'ended' never fires
    } else {
      // Animated APNG autoplays once on its own (loop=1). Fade shortly after it completes (~10s clip).
      timer = setTimeout(dismiss, 11000);
    }
    return () => clearTimeout(timer);
  }, [show, platform]);

  if (!show) return null;

  const media = "h-auto w-[min(92vw,520px)] max-h-[85vh] object-contain";

  return (
    <div
      data-testid="welcome-animation-overlay"
      aria-hidden="true"
      className="fixed inset-0 z-[9999] flex items-center justify-center transition-opacity duration-500 ease-out"
      style={{ pointerEvents: "none", opacity: leaving ? 0 : 1 }}
    >
      {platform === "ios" ? (
        // iPhone/iOS/Safari: transparent animated APNG (real alpha) — NO <video>, no play button, no background.
        <img
          data-testid="welcome-animation-media"
          alt=""
          src="/brand/zrope-welcome.apng.png"
          className={media}
          style={{ background: "transparent" }}
        />
      ) : (
        <video
          ref={videoRef}
          data-testid="welcome-animation-media"
          autoPlay
          muted
          playsInline
          preload="auto"
          controls={false}
          disablePictureInPicture
          onEnded={dismiss}
          onError={dismiss}
          src="/brand/zrope-welcome.webm"
          className={media}
          style={{ background: "transparent" }}
        />
      )}
    </div>
  );
}
